import React, { useMemo } from "react";
import { PlayerMiniCard } from "../PlayerMiniCard";
import TeamSerialNumber from "../TeamSerialNumber";
import StatisticsImage from "../Cloudinary/StatisticsImage";
import { getTranslations, decodeName } from "../../Common/utils";
import { NoDataFound } from "../NoDataFound";

const MyResultsTable = ({
  tableData = null,
  userTeamNo = 1,
  userSocialId = null,
  onRowClick = () => {},
  selectedMember = null,
  style = {},
}) => {
  // Dummy data commented out - was used for UI development purposes
  // const dummyData = [
  //   {
  //     rank: 1,
  //     trend: 2, // 0 = equal, 2 = up, 3 = down
  //     teamName: "Team name",
  //     userName: "Player name",
  //     teamNo: 1,
  //     points: 1500,
  //     players: [...],
  //   },
  //   ...
  // ];

  const data = useMemo(() => {
    if (!tableData || !Array.isArray(tableData) || tableData.length === 0) {
      return [];
    }

    return tableData.map((row) => {
      return {
        rank: row.cur_rank ?? row.rank,
        trend: row.trend ?? 0,
        teamName: decodeName(decodeName(row.team_name ?? row.teamName ?? "")),
        userName: decodeName(decodeName(row.user_name ?? row.userName ?? "")),
        teamNo: row.team_no ?? row.teamNo,
        points: row.cur_points ?? row.points,
        players: row.players || [],
        drivers:
          row.players?.filter((player) => player.PositionName === "DRIVER") ||
          [],
        constructors:
          row.players?.filter(
            (player) => player.PositionName === "CONSTRUCTOR"
          ) || [],

        social_id: row.social_id,
        user_team: row.user_team,
        user_guid: row.user_guid,

        showTeamImages: row.players,
      };
    });
  }, [tableData]);

  const getRank = (rank) => {
    return isNaN(rank) ? "--" : rank;
  };

  const getPoints = (points) => {
    if (points === 0) {
      return 0;
    }
    return points || "--";
  };

  const getTrendIcon = (trend) => {
    // API trend values: -1 = no change/disabled, 0 = equal, 1 = up, 2 = up (alternative), 3 = down
    // Component expects: 0 = equal, 2 = up, 3 = down
    if (trend === -1) return "f1i-lbd-down";
    if (trend === 0) return "f1i-lbd-unchanged";
    if (trend === 1) return "f1i-lbd-up";
    return "";
  };

  // Show empty state when no data is available
  if (!data || data.length === 0) {
    return (
      <NoDataFound
        noDataCaption={getTranslations(
          "my_results_table_no_leaderboard",
          "No leaderboard found"
        )}
      />
    );
  }

  return (
    <div className="si-myResultsTable" style={style}>
      <table className="si-tbl">
        <thead>
          <tr>
            <th className="si-tbl si-tbl--position">
              <span className="si-myResultsTable__header-full">
                {getTranslations("my_results_table_position", "POSITION")}
              </span>
              <span className="si-myResultsTable__header-short">
                {getTranslations("my_results_table_position_short", "POS.")}
              </span>
            </th>
            <th className="si-tbl si-tbl--name">
              {getTranslations("my_results_table_name", "NAME")}
            </th>
            <th className="si-tbl si-tbl--points">
              <span className="si-myResultsTable__header-full">
                {getTranslations("my_results_table_points", "PTS")}
              </span>
              <span className="si-myResultsTable__header-short">
                {getTranslations("my_results_table_points_short", "PTS.")}
              </span>
            </th>
            <th className="si-tbl si-tbl--team">
              {getTranslations("my_results_table_team", "TEAM")}
            </th>
            <th className="si-tbl si-tbl--arrow"></th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => {
            const isUserRow =
              userSocialId &&
              row.social_id &&
              String(userSocialId) === String(row.social_id);

            const selectedMemberTeamNo =
              selectedMember?.teamNo ?? selectedMember?.team_no;
            const isSelectedRow =
              selectedMember &&
              selectedMember.social_id &&
              row.social_id &&
              selectedMemberTeamNo !== undefined &&
              row.teamNo !== undefined &&
              String(selectedMember.social_id) === String(row.social_id) &&
              Number(selectedMemberTeamNo) === Number(row.teamNo) &&
              !isUserRow;
            const rowClasses = [
              getRank(row.rank) === "--" || getPoints(row.points) === "--"
                ? "si-disabled"
                : "",
              isUserRow ? "si-user-row" : "",
              isSelectedRow ? "si-selected-row" : "",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <tr
                key={index}
                className={rowClasses}
                onClick={() => onRowClick(row)}
                style={{ cursor: "pointer" }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onRowClick(row);
                  }
                }}
                aria-label={`View details for ${row.teamName}`}
              >
                <td className="si-tbl si-tbl--position">
                  <div className="si-user__rank">
                    {getRank(row.rank) !== "--" && getTrendIcon(row.trend) && (
                      <i className={getTrendIcon(row.trend)}></i>
                    )}
                    <span>{getRank(row.rank)}</span>
                  </div>
                </td>

                <td className="si-tbl si-tbl--name">
                  <div className="si-myResultsTable__name">
                    {isUserRow && <TeamSerialNumber teamNumber={row.teamNo} />}
                    <PlayerMiniCard
                      PlayerMiniCardName={row.teamName}
                      PlayerMiniCardTeam={row.userName}
                      showImage={false}
                      isNameSplit={false}
                    />
                  </div>
                </td>

                <td className="si-tbl si-tbl--points">
                  {getPoints(row.points)}{" "}
                </td>

                <td className="si-tbl si-tbl--team">
                  <div className="si-myResultsTable__teamList">
                    <div className="si-myResultsTable__teamList-drivers">
                      {row.drivers &&
                        row.drivers.length > 0 &&
                        row.drivers.map((player, playerIndex) => (
                          <div
                            key={playerIndex}
                            x
                            className="si-myResultsTable__teamList-item"
                          >
                            <StatisticsImage statisticsPlayer={player} />
                          </div>
                        ))}
                    </div>
                    <div className="si-myResultsTable__teamList-constructors">
                      {row.constructors &&
                        row.constructors.length > 0 &&
                        row.constructors.map((player, playerIndex) => (
                          <div
                            key={playerIndex}
                            className="si-myResultsTable__teamList-item"
                          >
                            <StatisticsImage statisticsPlayer={player} />
                          </div>
                        ))}
                    </div>
                  </div>
                </td>

                <td className="si-tbl si-tbl--arrow">
                  <div className="si-myResultsTable__arrow-btn">
                    <i className="f1i-chevron-right"></i>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default MyResultsTable;
